export type SpringSpec = { duration: number; bounce: number };

export const springs = {
  press: { duration: 170, bounce: 0.2 },
  enter: { duration: 250, bounce: 0.2 },
  exit: { duration: 130, bounce: 0 },
  expand: { duration: 250, bounce: 0 },
  overlay: { duration: 350, bounce: 0 },
  reveal: { duration: 450, bounce: 0 },
  fade: { duration: 150, bounce: 0 },
  expressive: { duration: 400, bounce: 0.3 },
} as const satisfies Record<string, SpringSpec>;

export type SpringName = keyof typeof springs;

type Physics = { mass: number; stiffness: number; damping: number };

export const toPhysics = ({ duration, bounce }: SpringSpec): Physics => {
  if (!(duration > 0)) {
    throw new RangeError(`spring duration must be > 0, got ${duration}`);
  }
  if (bounce < 0 || bounce >= 1) {
    throw new RangeError(`spring bounce must be in [0, 1), got ${bounce}`);
  }
  const omega = (2 * Math.PI) / (duration / 1000);
  return {
    mass: 1,
    stiffness: omega * omega,
    damping: 2 * (1 - bounce) * omega,
  };
};

const positionAt = (seconds: number, { stiffness, damping }: Physics) => {
  const omega = Math.sqrt(stiffness);
  const zeta = damping / (2 * omega);
  if (zeta >= 1) {
    return 1 - Math.exp(-omega * seconds) * (1 + omega * seconds);
  }
  const omegaD = omega * Math.sqrt(1 - zeta * zeta);
  return (
    1 -
    Math.exp(-zeta * omega * seconds) *
      (Math.cos(omegaD * seconds) +
        ((zeta * omega) / omegaD) * Math.sin(omegaD * seconds))
  );
};

const SAMPLES = 40;
const SETTLE_TOLERANCE = 0.002;

// Use for JS timers that wait on a transition.
export const settleMs = (spec: SpringSpec) => {
  const physics = toPhysics(spec);
  let settle = 0;
  for (let ms = 0; ms <= 6000; ms += 4) {
    if (Math.abs(positionAt(ms / 1000, physics) - 1) > SETTLE_TOLERANCE) {
      settle = ms + 4;
    }
  }
  return settle;
};

export const toCssTransition = (spec: SpringSpec) => {
  const physics = toPhysics(spec);
  const settle = settleMs(spec);
  const points = Array.from({ length: SAMPLES + 1 }, (_, i) =>
    i === SAMPLES
      ? 1
      : Number(positionAt((settle / 1000) * (i / SAMPLES), physics).toFixed(4)),
  );
  return `${settle}ms linear(${points.join(", ")})`;
};

// Web reduced motion: replaces the whole `transition` shorthand on spring-driven
// elements (movement snaps, opacity and colour ride `fade`). Overriding
// `transition-property` alone would reindex the duration list.
export const reducedMotionCss = (() => {
  const fade = toCssTransition(springs.fade);
  const props = ["opacity", "color", "background-color", "border-color"];
  const rule = `{transition:${props
    .map(prop => `${prop} ${fade}`)
    .join(",")} !important}`;
  return `@media (prefers-reduced-motion: reduce){[style*="linear("]${rule}}`;
})();
